import { getDamEnvFromScene7File } from './dam-env-scene7file';
import { getNumericAspectRatio } from './getNumericAspectRatio';
import { getServerOptions } from './getServerOptions';
const SCENE7_IS_IMAGE_PATH = '/is/image/';
export const DEFAULT_LOSSY_IMAGE_QUALITY = 79;
export const DEFAULT_FORMAT = 'png-alpha';
export const LOSSY_FORMATS = [
    'jpeg',
    'jpg',
    'webp',
    'webp-alpha'
];
export const FOCAL_POINT_VERTICAL_POSITION = {
    top: 2 / 3,
    center: 1 / 2,
    bottom: 1 / 3
};
export const FOCAL_POINT_HORIZONTAL_POSITION = {
    left: 1 / 3,
    center: 1 / 2,
    right: 2 / 3
};
export function getScene7ImageURL(requestedImage, requiredWidth, config, logger) {
    if (requestedImage.imageSourceType === 'dam') {
        return requestedImage.contentFile;
    }
    if (!config.scene7Config.serviceEnabled || !requestedImage.scene7File) {
        return requestedImage.contentFile;
    }
    const params = requestedImage.params || {};
    const damEnv = getDamEnvFromScene7File(requestedImage.scene7File);
    const serverOptions = getServerOptions(requestedImage.metaData ? requestedImage.metaData.scene7Domain : undefined, config, params.scene7ServerMandatory, damEnv);
    const useSmartImaging = isUseSmartImaging(params, serverOptions, config);
    const format = params.format
        ? params.format
        : DEFAULT_FORMAT;
    const quality = params.quality
        ? params.quality
        : DEFAULT_LOSSY_IMAGE_QUALITY;
    const cropping = 'fit=crop,1';
    const formatParam = useSmartImaging ? undefined : `fmt=${format}`;
    const qualityParam = isFormatLossy(format) ? `qlt=${quality}` : undefined;
    const dimensions = calculateDimensions(requiredWidth, config, params.aspectRatio, requestedImage.metaData);
    const width = `wid=${dimensions.width}`;
    const height = dimensions.height ? `hei=${dimensions.height}` : undefined;
    const align = params.focalPoint
        ? `align=${getAlignment(params.focalPoint)}`
        : undefined;
    const smartImaging = useSmartImaging ? undefined : 'bfc=off';
    let modifiers = Array.of(cropping, formatParam, qualityParam, width, height, align, smartImaging)
        .filter(keyVal => keyVal !== undefined)
        .join('&');
    const rawModifiers = modifiers;
    if (serverOptions.lockingEnabled) {
        modifiers = `${modifiers}&${calculateLockingValue(modifiers)}`;
    }
    if (serverOptions.obfuscationEnabled) {
        modifiers = btoa(modifiers);
    }
    const urlAndPath = serverOptions.domain + SCENE7_IS_IMAGE_PATH + requestedImage.scene7File;
    logger.debug('image:', requestedImage.scene7File, ' - ', rawModifiers);
    return encodeURI(`${urlAndPath}?${modifiers}`);
}
function isFormatLossy(format) {
    return LOSSY_FORMATS.includes(format);
}
/**
 * Locking algorithm as described at
 * https://marketing.adobe.com/resources/help/en_US/s7/is_ir_api/is_api/http_ref/r_request_locking.html
 */
function calculateLockingValue(from) {
    let sum = 0;
    for (let i = 0; i < from.length; i++) {
        // number-literal-format disabled due to prettier which makes it lowercase
        // tslint:disable-next-line:no-bitwise number-literal-format
        sum = (sum * 131 + from.charCodeAt(i)) & 0xffff;
    }
    const hexa = sum.toString(16);
    return hexa.padStart(4, '0');
}
function calculateDimensions(targetWidth, config, aspectRatio, imageMetaData) {
    const maxWidth = imageMetaData && imageMetaData.originalWidth !== 0
        ? Math.min(imageMetaData.originalWidth, config.maxWidth)
        : config.maxWidth;
    const maxHeight = imageMetaData && imageMetaData.originalHeight !== 0
        ? Math.min(imageMetaData.originalHeight, config.maxHeight)
        : config.maxHeight;
    // width and height, width-restricted
    let width = Math.min(maxWidth, targetWidth);
    let height = aspectRatio
        ? Math.round(width / getNumericAspectRatio(aspectRatio))
        : undefined;
    if (typeof height === 'undefined') {
        return { width, height };
    }
    // recalc if height is too large
    if (aspectRatio && height >= 0 && height > maxHeight) {
        height = maxHeight;
        width = Math.round(height * getNumericAspectRatio(aspectRatio));
    }
    return { width, height };
}
function getAlignment(focalPoint) {
    return `${getFocalPointAlignX(focalPoint)},${getFocalPointAlignY(focalPoint)}`;
}
function getFocalPointAlignX(focalPoint) {
    return Math.min(1, Math.max(-1, (FOCAL_POINT_HORIZONTAL_POSITION[focalPoint.x] - 0.5) * 5)).toFixed(2);
}
function getFocalPointAlignY(focalPoint) {
    return Math.min(1, Math.max(-1, (FOCAL_POINT_VERTICAL_POSITION[focalPoint.y] - 0.5) * 5)).toFixed(2);
}
function isUseSmartImaging(params, serverOptions, config) {
    // smart imaging only works on the live domain.
    return Boolean(params.useSmartImaging &&
        serverOptions.domain === config.scene7Config.liveDomain);
}
